import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'sonner';
import { Check, Lock, ArrowRight, Zap, Star, Rocket, ChevronLeft, X, ShieldCheck, Clock } from 'lucide-react';
import { updateCompanyPlan, setPlanRequestStatus } from '../../../store/slices/companySlice';
import { cn } from '../../../lib/cn';
import { authApi } from '../../../api/axios';

const PLANS = [
  {
    id: 'BASIC',
    name: 'Basic',
    tagline: 'For small teams getting started',
    price: '₹0',
    priceSub: 'Free forever',
    employeeLimit: '5 employees',
    color: '#6C6C70',
    bgMuted: 'rgba(108,108,112,0.08)',
    icon: Zap,
    included: [
      'Up to 5 employees',
      'Manual attendance marking',
      'Basic HR reports',
      'Team chat',
      'Employee profiles',
      'Leave requests',
    ],
    locked: [
      'GPS / Selfie / QR attendance',
      'Payroll processing',
      'Lens kiosk devices',
      'Expense management',
      'Compliance filings (PF/ESIC/TDS)',
      'Recruitment ATS',
      'Advanced analytics',
    ],
  },
  {
    id: 'PRO',
    name: 'Pro',
    tagline: 'For growing businesses',
    price: '₹149',
    priceSub: 'per employee / month',
    employeeLimit: '100 employees',
    color: '#007AFF',
    bgMuted: 'rgba(0,122,255,0.08)',
    icon: Star,
    popular: true,
    requiresApproval: true,
    included: [
      'Up to 100 employees',
      'No. of companies allowed: 1',
      'All attendance methods (GPS, Selfie+Face, QR)',
      'Full payroll — PF, ESIC, TDS, PT',
      'Expense claims & Khata ledger',
      'Compliance filing tracker',
      'Recruitment ATS pipeline',
      'Up to 5 Lens kiosk devices',
      'Advanced analytics & dashboards',
      'Custom salary structures',
      'White-label payslip branding',
    ],
    locked: [
      'Unlimited employees',
      'Multiple companies (workspace switcher)',
      'Unlimited Lens devices',
      'API access',
      'Dedicated account manager',
    ],
  },
  {
    id: 'CUSTOM',
    name: 'Custom',
    tagline: 'For large & multi-company organizations',
    price: 'Custom',
    priceSub: 'Contact our sales team',
    employeeLimit: 'Unlimited employees',
    color: '#AF52DE',
    bgMuted: 'rgba(175,82,222,0.08)',
    icon: Rocket,
    isSalesAssisted: true,
    included: [
      'Unlimited employees',
      'Unlimited companies — workspace switcher',
      'Everything in Pro',
      'Unlimited Lens kiosk devices',
      'REST API access',
      'Custom roles & permissions',
      'Dedicated account manager',
      'SLA-backed priority support',
      'Custom onboarding & training',
    ],
    locked: [],
  },
];

function PlanCard({ plan, onViewDetails }) {
  const Icon = plan.icon;
  return (
    <div
      className={cn(
        'relative flex flex-col rounded-[20px] border-2 p-6 cursor-pointer transition-all hover:shadow-[var(--shadow-lg)] hover:-translate-y-0.5',
        plan.popular
          ? 'border-[var(--accent)] shadow-[0_0_0_4px_rgba(0,122,255,0.1)]'
          : 'border-[var(--border)] hover:border-opacity-60'
      )}
      style={{ backgroundColor: 'var(--bg-card)' }}
    >
      {plan.popular && (
        <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-[11px] font-bold text-white whitespace-nowrap"
          style={{ backgroundColor: plan.color }}>
          Most Popular
        </div>
      )}

      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="w-10 h-10 rounded-[12px] flex items-center justify-center shrink-0" style={{ backgroundColor: plan.bgMuted }}>
          <Icon size={18} style={{ color: plan.color }} />
        </div>
      </div>

      <h3 className="text-[20px] font-black text-[var(--text-primary)]">{plan.name}</h3>
      <p className="text-[12px] text-[var(--text-secondary)] mt-0.5 mb-4">{plan.tagline}</p>

      {/* Price */}
      <div className="mb-5">
        <span className="text-[32px] font-black" style={{ color: plan.color }}>{plan.price}</span>
        <p className="text-[11px] text-[var(--text-tertiary)] mt-0.5">{plan.priceSub}</p>
        <p className="text-[11px] font-semibold text-[var(--text-secondary)] mt-1">{plan.employeeLimit}</p>
      </div>

      {/* Top features */}
      <div className="space-y-2 flex-1 mb-6">
        {plan.included.slice(0, 4).map(f => (
          <div key={f} className="flex items-start gap-2">
            <Check size={13} className="mt-0.5 shrink-0" style={{ color: plan.color }} />
            <span className="text-[12px] text-[var(--text-secondary)]">{f}</span>
          </div>
        ))}
        {plan.locked.slice(0, 2).map(f => (
          <div key={f} className="flex items-start gap-2 opacity-40">
            <Lock size={13} className="mt-0.5 shrink-0 text-[var(--text-tertiary)]" />
            <span className="text-[12px] text-[var(--text-tertiary)] line-through">{f}</span>
          </div>
        ))}
      </div>

      {/* CTA */}
      <button
        onClick={() => onViewDetails(plan)}
        className="w-full py-2.5 rounded-[12px] text-[13px] font-bold transition-all flex items-center justify-center gap-2"
        style={{
          backgroundColor: plan.popular ? plan.color : plan.bgMuted,
          color: plan.popular ? '#fff' : plan.color,
        }}
      >
        View Details <ArrowRight size={14} />
      </button>
    </div>
  );
}

function PlanDetailModal({ plan, onClose, onSelect }) {
  const Icon = plan.icon;
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-6">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full sm:max-w-lg bg-[var(--bg-card)] rounded-t-[24px] sm:rounded-[24px] shadow-[var(--shadow-lg)] max-h-[90vh] overflow-y-auto fade-in">
        {/* Header */}
        <div className="sticky top-0 bg-[var(--bg-card)] border-b border-[var(--border)] px-6 pt-5 pb-4 rounded-t-[24px]">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-[12px] flex items-center justify-center" style={{ backgroundColor: plan.bgMuted }}>
                <Icon size={18} style={{ color: plan.color }} />
              </div>
              <div>
                <h2 className="text-[18px] font-black text-[var(--text-primary)]">{plan.name} Plan</h2>
                <p className="text-[12px] text-[var(--text-secondary)]">{plan.priceSub}</p>
              </div>
            </div>
            <button onClick={onClose} className="w-8 h-8 rounded-full bg-[var(--bg-muted)] flex items-center justify-center text-[var(--text-secondary)] hover:text-[var(--text-primary)]">
              <X size={14} />
            </button>
          </div>
          <div className="mt-3">
            <span className="text-[28px] font-black" style={{ color: plan.color }}>{plan.price}</span>
            {plan.price !== 'Custom' && <span className="text-[13px] text-[var(--text-secondary)] ml-1">/employee/month</span>}
            <p className="text-[11px] font-semibold text-[var(--text-secondary)] mt-0.5">{plan.employeeLimit}</p>
          </div>
          {plan.requiresApproval && (
            <div className="mt-3 flex items-center gap-2 px-3 py-2 rounded-[10px] bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
              <Clock size={13} className="text-amber-600 dark:text-amber-400 shrink-0" />
              <span className="text-[12px] text-amber-700 dark:text-amber-400">Activated after a quick AttendX team review (within 1 business hour)</span>
            </div>
          )}
          {plan.isSalesAssisted && (
            <div className="mt-3 flex items-center gap-2 px-3 py-2 rounded-[10px] bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800">
              <Clock size={13} className="text-purple-600 dark:text-purple-400 shrink-0" />
              <span className="text-[12px] text-purple-700 dark:text-purple-400">Our team will reach out to set this up — no payment now</span>
            </div>
          )}
        </div>

        {/* Feature list */}
        <div className="px-6 py-4">
          {plan.included.length > 0 && (
            <div className="mb-5">
              <p className="text-[11px] font-bold uppercase tracking-widest text-[var(--text-tertiary)] mb-3">What's Included</p>
              <div className="space-y-3">
                {plan.included.map(f => (
                  <div key={f} className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-full flex items-center justify-center shrink-0 mt-0.5" style={{ backgroundColor: plan.bgMuted }}>
                      <Check size={11} style={{ color: plan.color }} />
                    </div>
                    <span className="text-[13px] text-[var(--text-primary)]">{f}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {plan.locked.length > 0 && (
            <div className="mb-5">
              <p className="text-[11px] font-bold uppercase tracking-widest text-[var(--text-tertiary)] mb-3">Not Included</p>
              <div className="space-y-3">
                {plan.locked.map(f => (
                  <div key={f} className="flex items-start gap-3 opacity-50">
                    <div className="w-5 h-5 rounded-full bg-[var(--bg-muted)] flex items-center justify-center shrink-0 mt-0.5">
                      <Lock size={10} className="text-[var(--text-tertiary)]" />
                    </div>
                    <span className="text-[13px] text-[var(--text-tertiary)]">{f}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="sticky bottom-0 bg-[var(--bg-card)] border-t border-[var(--border)] px-6 py-4 space-y-3">
          <button
            onClick={() => onSelect(plan.id)}
            className="w-full py-3 rounded-[14px] text-[14px] font-bold text-white transition-all hover:opacity-90 active:scale-[0.98] flex items-center justify-center gap-2"
            style={{ backgroundColor: plan.color }}
          >
            {plan.isSalesAssisted ? 'Request Custom Plan' : `Start with ${plan.name}`}
            <ArrowRight size={16} />
          </button>
          <button onClick={onClose} className="w-full flex items-center justify-center gap-1.5 text-[12px] text-[var(--text-secondary)] hover:text-[var(--text-primary)]">
            <ChevronLeft size={13} /> Compare all plans
          </button>
        </div>
      </div>
    </div>
  );
}

// Dummy payment screen — used for Pro only. Card fields are cosmetic (no
// real validation, no real charge). On submit, the company is granted
// Basic-tier access immediately while the Pro upgrade waits for a Super
// Admin to approve it (see ProPendingBanner in the dashboard layout).
function ProPaymentScreen({ plan, onConfirm, onBack }) {
  const [cardNumber, setCardNumber] = useState('');
  const [expiry, setExpiry] = useState('');
  const [cvv, setCvv] = useState('');
  const [activating, setActivating] = useState(false);
  const [done, setDone] = useState(false);

  const amount = 149 * 1; // demo seat price, real per-employee billing comes with the actual headcount

  const handlePay = async () => {
    setActivating(true);
    await new Promise(r => setTimeout(r, 1500));
    setDone(true);
    setTimeout(() => onConfirm(amount * 100), 900);
  };

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="bg-[var(--bg-card)] rounded-[24px] border border-[var(--border)] shadow-[var(--shadow-lg)] overflow-hidden">
        {/* header */}
        <div className="px-6 pt-6 pb-5 border-b border-[var(--border)]" style={{ background: 'linear-gradient(135deg,#007AFF18,transparent)' }}>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-[12px] flex items-center justify-center" style={{ backgroundColor: '#007AFF20' }}>
              <ShieldCheck size={20} style={{ color: '#007AFF' }} />
            </div>
            <div>
              <p className="text-[11px] font-bold uppercase tracking-widest text-[var(--text-tertiary)]">Upgrading to</p>
              <h2 className="text-[18px] font-black text-[var(--text-primary)]">{plan.name} Plan</h2>
            </div>
          </div>
          <div className="flex items-center gap-2 px-3 py-2 rounded-[10px] bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
            <span className="text-amber-600 text-[13px] font-bold">⚡ Test Mode</span>
            <span className="text-amber-700 dark:text-amber-400 text-[12px]">— no real payment will be charged</span>
          </div>
        </div>

        {/* body */}
        <div className="px-6 py-6 space-y-4">
          {done ? (
            <div className="flex flex-col items-center py-4 gap-2">
              <div className="w-12 h-12 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                <Check size={24} className="text-green-600"/>
              </div>
              <p className="text-[15px] font-bold text-[var(--text-primary)]">Payment received!</p>
              <p className="text-[12px] text-[var(--text-secondary)] text-center">Taking you to your dashboard — Pro unlocks once our team confirms.</p>
            </div>
          ) : (
            <>
              <div className="space-y-1.5">
                <label className="text-[12px] font-semibold text-[var(--text-primary)]">Card Number</label>
                <input value={cardNumber} maxLength={19}
                  onChange={e => setCardNumber(e.target.value.replace(/[^\d ]/g, ''))}
                  placeholder="4242 4242 4242 4242"
                  className="w-full h-10 px-3 bg-[var(--bg-card)] border border-[var(--border)] rounded-[10px] text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)] transition-all"/>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <label className="text-[12px] font-semibold text-[var(--text-primary)]">Expiry</label>
                  <input value={expiry} maxLength={5}
                    onChange={e => setExpiry(e.target.value.replace(/[^\d/]/g, ''))}
                    placeholder="MM/YY"
                    className="w-full h-10 px-3 bg-[var(--bg-card)] border border-[var(--border)] rounded-[10px] text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)] transition-all"/>
                </div>
                <div className="space-y-1.5">
                  <label className="text-[12px] font-semibold text-[var(--text-primary)]">CVV</label>
                  <input value={cvv} maxLength={3} type="password"
                    onChange={e => setCvv(e.target.value.replace(/\D/g, ''))}
                    placeholder="123"
                    className="w-full h-10 px-3 bg-[var(--bg-card)] border border-[var(--border)] rounded-[10px] text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)] transition-all"/>
                </div>
              </div>

              <div className="rounded-[12px] bg-[var(--bg-muted)] p-4 space-y-2">
                <div className="flex justify-between text-[13px]">
                  <span className="text-[var(--text-secondary)]">Plan</span>
                  <span className="font-semibold text-[var(--text-primary)]">{plan.name}</span>
                </div>
                <div className="flex justify-between text-[13px]">
                  <span className="text-[var(--text-secondary)]">Price</span>
                  <span className="font-semibold text-[var(--text-primary)]">₹149/employee/month</span>
                </div>
                <div className="flex justify-between text-[13px] pt-2 border-t border-[var(--border)]">
                  <span className="text-[var(--text-secondary)] font-semibold">Pay now (test)</span>
                  <span className="font-bold text-[var(--text-primary)]">₹{amount}</span>
                </div>
              </div>

              <button onClick={handlePay} disabled={activating}
                className="w-full py-3.5 rounded-[14px] text-[14px] font-bold text-white transition-all hover:opacity-90 active:scale-[0.98] disabled:opacity-60 flex items-center justify-center gap-2"
                style={{ backgroundColor: '#007AFF' }}>
                {activating ? (
                  <><span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin"/>Processing…</>
                ) : (
                  <>Pay ₹{amount} <ArrowRight size={16}/></>
                )}
              </button>
            </>
          )}

          {!done && (
            <button onClick={onBack} className="w-full text-center text-[12px] text-[var(--text-secondary)] hover:text-[var(--text-primary)] flex items-center justify-center gap-1">
              <ChevronLeft size={13}/> Back to plans
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// Shown after a Custom plan request is auto-submitted (registered users
// skip any form — we already know their details). Just confirms and
// auto-continues into the dashboard.
function CustomRequestScreen() {
  return (
    <div className="w-full max-w-md mx-auto">
      <div className="bg-[var(--bg-card)] rounded-[24px] border border-[var(--border)] shadow-[var(--shadow-lg)] overflow-hidden px-6 py-10 flex flex-col items-center text-center gap-3">
        <div className="w-14 h-14 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
          <Check size={28} className="text-purple-600"/>
        </div>
        <p className="text-[17px] font-bold text-[var(--text-primary)]">Request received!</p>
        <p className="text-[13px] text-[var(--text-secondary)] max-w-xs">
          Our team will reach out within 1 business hour to set up your Custom plan and enable multi-company access.
        </p>
        <p className="text-[12px] text-[var(--text-tertiary)] mt-2">Taking you to your dashboard…</p>
      </div>
    </div>
  );
}

export default function SelectPlanPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const accessToken = useSelector(s => s.auth.accessToken);
  const user = useSelector(s => s.auth.user);
  const company = useSelector(s => s.company);
  const [detailPlan, setDetailPlan] = useState(null);
  const [paymentPlan, setPaymentPlan] = useState(null);
  const [customSubmitted, setCustomSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);

  const authHeaders = { headers: { Authorization: `Bearer ${accessToken}` } };

  const handleSelect = async (planId) => {
    const plan = PLANS.find(p => p.id === planId);
    setDetailPlan(null);
    if (planId === 'BASIC') {
      await savePlanAndNavigate('BASIC');
    } else if (planId === 'PRO') {
      setPaymentPlan(plan);
    } else if (planId === 'CUSTOM') {
      await requestCustomAndNavigate();
    }
  };

  const savePlanAndNavigate = async (planId) => {
    setSaving(true);
    try {
      await authApi.patch('/v1/auth/company/plan', { plan: planId }, authHeaders);
    } catch (e) {
      console.warn('Plan save failed (DB may not be connected):', e.message);
    } finally {
      setSaving(false);
    }
    dispatch(updateCompanyPlan(planId));
    navigate('/dashboard');
  };

  // Pro lands on Basic-tier access immediately; the actual Pro features
  // unlock once a Super Admin approves the request (Phase 4 console).
  const requestProAndNavigate = async (dummyAmount) => {
    setSaving(true);
    try {
      await authApi.patch('/v1/auth/company/plan', { plan: 'BASIC' }, authHeaders);
      await authApi.post('/v1/auth/plan-request', {
        plan: 'PRO', type: 'PRO_UPGRADE',
        dummy_payment_completed: true, dummy_payment_amount: dummyAmount,
      }, authHeaders);
      dispatch(updateCompanyPlan('BASIC'));
      dispatch(setPlanRequestStatus('PENDING_PRO_APPROVAL'));
      navigate('/dashboard');
    } catch (e) {
      console.error('Pro plan request failed:', e.message);
      toast.error('Could not submit your Pro request — please check your connection and try again.');
      setPaymentPlan(null);
    } finally {
      setSaving(false);
    }
  };

  // Custom is always the "registered customer — direct request" path here,
  // since this page only exists for an authenticated, freshly-registered
  // user — no callback form, we already have their details.
  const requestCustomAndNavigate = async () => {
    setSaving(true);
    try {
      await authApi.patch('/v1/auth/company/plan', { plan: 'BASIC' }, authHeaders);
      await authApi.post('/v1/auth/plan-request', {
        plan: 'CUSTOM', type: 'CUSTOM_REGISTERED',
        contact_name: user?.name, contact_company_name: company?.name,
        contact_phone: user?.phone, contact_email: user?.email,
      }, authHeaders);
      dispatch(updateCompanyPlan('BASIC'));
      dispatch(setPlanRequestStatus('PENDING_CUSTOM_APPROVAL'));
      setCustomSubmitted(true);
      setTimeout(() => navigate('/dashboard'), 1800);
    } catch (e) {
      console.error('Custom plan request failed:', e.message);
      toast.error('Could not submit your Custom plan request — please check your connection and try again.');
    } finally {
      setSaving(false);
    }
  };

  if (customSubmitted) {
    return (
      <div className="w-full max-w-5xl px-4 py-8 flex flex-col items-center">
        <div className="flex flex-col items-center mb-8">
          <div className="w-14 h-14 rounded-[18px] bg-[var(--accent)] flex items-center justify-center mb-3 shadow-[var(--shadow-md)]">
            <span className="text-white text-2xl font-black">A</span>
          </div>
          <h1 className="text-[24px] font-black text-[var(--text-primary)]">AttendX</h1>
        </div>
        <CustomRequestScreen />
      </div>
    );
  }

  if (paymentPlan) {
    return (
      <div className="w-full max-w-5xl px-4 py-8 flex flex-col items-center">
        <div className="flex flex-col items-center mb-8">
          <div className="w-14 h-14 rounded-[18px] bg-[var(--accent)] flex items-center justify-center mb-3 shadow-[var(--shadow-md)]">
            <span className="text-white text-2xl font-black">A</span>
          </div>
          <h1 className="text-[24px] font-black text-[var(--text-primary)]">Almost there!</h1>
          <p className="text-[13px] text-[var(--text-secondary)] mt-1">Confirm your plan to start using AttendX</p>
        </div>
        <ProPaymentScreen
          plan={paymentPlan}
          onConfirm={requestProAndNavigate}
          onBack={() => setPaymentPlan(null)}
        />
      </div>
    );
  }

  return (
    <div className="w-full max-w-5xl px-4 py-8">
      {/* AttendX branding */}
      <div className="flex flex-col items-center mb-10">
        <div className="w-14 h-14 rounded-[18px] bg-[var(--accent)] flex items-center justify-center mb-3 shadow-[var(--shadow-md)]">
          <span className="text-white text-2xl font-black">A</span>
        </div>
        <h1 className="text-[28px] font-black text-[var(--text-primary)]">AttendX</h1>
        <p className="text-[14px] text-[var(--text-secondary)] mt-1">Choose the right plan for your business</p>
        <p className="text-[12px] text-[var(--text-tertiary)] mt-1">You can change your plan any time from Settings</p>
      </div>

      {/* Plan cards grid */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
        {PLANS.map(plan => (
          <PlanCard key={plan.id} plan={plan} onViewDetails={setDetailPlan} />
        ))}
      </div>

      {/* Footer note */}
      <p className="text-center text-[11px] text-[var(--text-tertiary)] mt-8">
        Basic is free forever · Pro activates after a quick team review · Custom is sales-assisted
      </p>

      {/* Detail modal */}
      {detailPlan && (
        <PlanDetailModal
          plan={detailPlan}
          onClose={() => setDetailPlan(null)}
          onSelect={handleSelect}
        />
      )}
    </div>
  );
}
